import React, { useState, useMemo } from "react";
import { Icon } from "../UIElements/Icon";
import { useUpdateAddressDefault } from "../../../hooks/useUpdateAddressDefault";

const AddressesTab = ({ addresses = [], userId, onUpdate }) => {
  const [updatingAddressId, setUpdatingAddressId] = useState(null);
  const updateAddressMutation = useUpdateAddressDefault();

  const filteredAddresses = useMemo(() => {
    if (!addresses || addresses.length === 0) return [];
    const uniqueAddresses = addresses.filter(
      (address, i, self) =>
        i ===
        self.findIndex(
          (obj) =>
            address.address === obj.address &&
            address.address_notes === obj.address_notes &&
            address.zipcode === obj.zipcode &&
            address.internal_number === obj.internal_number,
        ),
    );

    return uniqueAddresses.sort((a, b) => {
      return a.id - b.id;
    });
  }, [addresses]);

  const handleAddressChange = async (addressId) => {
    setUpdatingAddressId(addressId);
    try {
      await updateAddressMutation.mutateAsync({
        userId,
        addressId,
        default: true,
      });
      if (onUpdate) {
        onUpdate();
      }
    } catch {
      // Error is already handled in the hook
    } finally {
      setUpdatingAddressId(null);
    }
  };

  // Helper function to build full address string
  const buildFullAddress = (address) => {
    // If we have a complete address field, use it directly
    if (address.address) {
      return address.address;
    }

    // Otherwise, build from individual components
    const parts = [];

    if (address.street_number) parts.push(address.street_number);
    if (address.route) parts.push(address.route);
    if (address.neighborhood) parts.push(address.neighborhood);
    if (address.sublocality) parts.push(address.sublocality);
    if (address.locality) parts.push(address.locality);
    if (address.city && address.city !== address.locality)
      parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);

    return parts.length > 0 ? parts.join(", ") : "No address available";
  };

  // Helper function to get tag label
  const getTagLabel = (tag) => {
    const tagLabels = {
      home: "Home",
      office: "Office",
      other: "Other",
    };
    return tagLabels[tag] || tag || "Other";
  };

  if (!filteredAddresses || filteredAddresses.length === 0) {
    return (
      <div className="space-y-6">
        <div className="py-12 text-center">
          <Icon
            name="MapPin"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mb-2 text-lg font-semibold">
            No Addresses Found
          </h3>
          <p className="text-muted-foreground">
            This user doesn&apos;t have any addresses configured.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {filteredAddresses.map((address) => {
          const fullAddress = buildFullAddress(address);
          const isDefault = address.default === true;
          const isUpdating = updatingAddressId === address.id;

          return (
            <div
              key={address.id}
              className={`bg-card rounded-lg border p-4 transition-colors ${
                isDefault
                  ? "border-primary/40 bg-accent"
                  : "border-border hover:border-border"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <input
                    type="radio"
                    id={`address-${address.id}`}
                    name="default-address"
                    checked={isDefault}
                    onChange={() => handleAddressChange(address.id)}
                    disabled={isUpdating || updateAddressMutation.isPending}
                    className="border-border bg-muted text-primary focus:ring-ring h-4 w-4 cursor-pointer focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        {isDefault && (
                          <span className="border-primary/40 bg-accent text-primary rounded border px-2 py-0.5 text-xs font-medium">
                            Default
                          </span>
                        )}
                        {address.tag && (
                          <span className="bg-muted text-foreground rounded px-2 py-0.5 text-xs font-medium">
                            {getTagLabel(address.tag)}
                          </span>
                        )}
                      </div>
                      <label
                        htmlFor={`address-${address.id}`}
                        className="text-foreground mb-1 block cursor-pointer font-medium"
                      >
                        {fullAddress}
                      </label>
                      {address.zipcode && (
                        <p className="text-muted-foreground mt-1 text-sm">
                          ZIP Code: {address.zipcode}
                        </p>
                      )}
                      {address.address_notes && (
                        <p className="text-muted-foreground mt-1 text-sm italic">
                          Notes: {address.address_notes}
                        </p>
                      )}
                    </div>
                    {isUpdating && (
                      <div className="flex-shrink-0">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-b-2"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddressesTab;
